import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { RedoSupportAuthRpcClientContext } from "@redotech/redo-merchant-app-common/support/redo-support-rpc-client-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { ShopperAiSettingsOptimization } from "@redotech/redo-model/shopper-ai-settings-optimization";
import { groupMap } from "@redotech/util/map";
import { useContext, useEffect, useState } from "react";
import { useDebounce } from "usehooks-ts";
import { SettingsOptimizationDefinition } from "./optimization-types";
import { settingsOptimizationDefinitions } from "./optimizations";
import {
  hasShownOptimizationThisSession,
  markOptimizationShownInSession,
} from "./session-storage-utils";

const WEEK_IN_HOURS = Temporal.Duration.from({ days: 7 }).total({
  unit: "hours",
});
const DEBOUNCE_MS = 1500;

function userEligibleToSeeOptimization(
  definition: SettingsOptimizationDefinition,
  config: ShopperAiSettingsOptimization | undefined,
): boolean {
  if (hasShownOptimizationThisSession()) {
    return false;
  }

  // If no config, this is the first time we're considering showing this optimization
  if (!config) {
    return true;
  }

  if (config.viewHistory.completed) {
    return false;
  }

  const retries = definition.retries || 0;

  if (config.viewHistory.dismissals > retries) {
    return false;
  }

  const inCooldownSinceLastDismissal =
    config.viewHistory.lastDismissal &&
    Temporal.Instant.compare(
      Temporal.Instant.from(
        typeof config.viewHistory.lastDismissal === "string"
          ? config.viewHistory.lastDismissal
          : config.viewHistory.lastDismissal.toISOString(),
      ),
      Temporal.Now.instant().subtract({ hours: WEEK_IN_HOURS }),
    ) > 0;

  if (inCooldownSinceLastDismissal) {
    return false;
  }

  return true;
}

export function useSettingsOptimizationManager() {
  const team = useContext(TeamContext);
  const user = useContext(UserContext);
  const supportRpcClient = useRequiredContext(RedoSupportAuthRpcClientContext);
  const [showOptimization, setShowOptimization] = useState(false);

  // Load optimization data from RPC
  const rpcOptimizationData = useLoad(async () => {
    try {
      return await supportRpcClient.getShopperAiSettingsOptimization(null);
    } catch (error) {
      console.error("Failed to load optimization data from server:", error);
      return [];
    }
  }, [supportRpcClient]);

  const optimizationToShow = useLoad(async () => {
    if (!team || !rpcOptimizationData.value) {
      return undefined;
    }

    const optimizationEvents = rpcOptimizationData.value;

    const groupedEvents = groupMap(optimizationEvents, (event) => [
      event.type,
      event,
    ]);

    const sortedDefinitions = Object.values(
      settingsOptimizationDefinitions,
    ).sort((a, b) => a.priority - b.priority);

    for (const definition of sortedDefinitions) {
      const userEligible = userEligibleToSeeOptimization(
        definition,
        groupedEvents.get(definition.type),
      );

      if (!userEligible) {
        continue;
      }

      let teamEligible;
      try {
        teamEligible = await definition.isEligible(
          team,
          user,
          supportRpcClient,
        );
      } catch (error) {
        console.error("Failed to check eligibility for optimization:", error);
        continue;
      }

      if (!teamEligible) {
        continue;
      }

      return definition.type;
    }

    return undefined;
  }, [team, user, rpcOptimizationData.value, supportRpcClient]);

  const optimizationType = useDebounce(optimizationToShow.value, DEBOUNCE_MS);

  const handleDismiss = useHandler(async () => {
    if (!optimizationType) {
      return;
    }

    setShowOptimization(false);

    const existingEvent = (rpcOptimizationData.value || []).find(
      (event) => event.type === optimizationType,
    );

    const eventToUpsert = existingEvent
      ? {
          type: existingEvent.type,
          viewHistory: {
            dismissals: (existingEvent.viewHistory.dismissals || 0) + 1,
            lastDismissal: new Date(),
            completed: existingEvent.viewHistory.completed,
          },
        }
      : {
          type: optimizationType,
          viewHistory: {
            dismissals: 1,
            lastDismissal: new Date(),
            completed: undefined,
          },
        };

    try {
      await supportRpcClient.upsertShopperAiSettingsOptimization(eventToUpsert);
    } catch (error) {
      console.error("Failed to update optimization data on server:", error);
    }
  });

  const handleComplete = useHandler(async () => {
    if (!optimizationType) {
      return;
    }

    setShowOptimization(false);

    const existingEvent = (rpcOptimizationData.value || []).find(
      (event) => event.type === optimizationType,
    );

    const eventToUpsert = existingEvent
      ? {
          type: existingEvent.type,
          viewHistory: {
            dismissals: existingEvent.viewHistory.dismissals,
            lastDismissal: existingEvent.viewHistory.lastDismissal,
            completed: new Date(),
          },
        }
      : {
          type: optimizationType,
          viewHistory: {
            dismissals: 0,
            lastDismissal: null,
            completed: new Date(),
          },
        };

    try {
      await supportRpcClient.upsertShopperAiSettingsOptimization(eventToUpsert);
    } catch (error) {
      console.error("Failed to update optimization data on server:", error);
    }
  });

  useEffect(() => {
    if (optimizationType) {
      setShowOptimization(true);
      markOptimizationShownInSession();
    }
  }, [optimizationType]);

  return { optimizationType, showOptimization, handleDismiss, handleComplete };
}
